import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Building2,
  Settings,
  FileText,
  ChevronLeft,
  ChevronRight,
  Wallet,
  BookOpen,
  Receipt,
  FileSpreadsheet,
  PiggyBank,
  PieChart,
  DollarSign,
  Clock,
  CalendarDays,
  ClipboardList,
  CheckSquare,
  Award,
  Megaphone,
  UserCheck,
  Target,
  Briefcase,
  Calendar,
  Layers,
  TrendingUp,
  FolderKanban,
  Video,
  Box,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext.js';
import './layout.css';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ collapsed, onToggleCollapse }) => {
  const { user, hasPermission } = useAuth();
  const [isHovered, setIsHovered] = useState(false);

  // Expand on hover if currently collapsed
  const isExpanded = !collapsed || isHovered;

  const projectItems = [
    { label: 'Operations Overview', path: '/projects/dashboard', icon: FolderKanban, show: hasPermission('PROJECTS', 'VIEW') },
    { label: 'Project Kanban & List', path: '/projects/list', icon: Briefcase, show: hasPermission('PROJECTS', 'VIEW') },
    { label: 'Deliverables & Approvals', path: '/projects/deliverables', icon: Video, show: hasPermission('PROJECTS', 'VIEW') },
    { label: 'Studio Resource Booking', path: '/projects/resources', icon: Box, show: hasPermission('PROJECTS', 'VIEW') },
  ];

  const crmItems = [
    { label: 'Sales & CRM Analytics', path: '/crm/dashboard', icon: TrendingUp, show: hasPermission('CRM', 'VIEW') },
    { label: 'Leads & Pipeline', path: '/crm/leads', icon: Target, show: hasPermission('CRM', 'VIEW') },
    { label: 'Client Directory', path: '/crm/clients', icon: Briefcase, show: hasPermission('CRM', 'VIEW') },
    { label: 'Quotations & Proposals', path: '/crm/quotations', icon: FileText, show: hasPermission('CRM', 'VIEW') },
    { label: 'Client Consultations', path: '/crm/meetings', icon: Calendar, show: hasPermission('CRM', 'VIEW') },
    { label: 'Studio Service Catalog', path: '/crm/services', icon: Layers, show: hasPermission('CRM', 'VIEW') },
  ];

  const hrmsItems = [
    { label: 'Workforce Overview', path: '/hrms/dashboard', icon: UserCheck, show: true },
    { label: 'Time & Attendance', path: '/hrms/attendance', icon: Clock, show: true },
    { label: 'Leave Management', path: '/hrms/leave', icon: CalendarDays, show: true },
    { label: 'Daily Work Reports', path: '/hrms/daily-reports', icon: ClipboardList, show: true },
    { label: 'Studio Task Board', path: '/hrms/tasks', icon: CheckSquare, show: true },
    { label: 'Performance Scorecard', path: '/hrms/performance', icon: Award, show: hasPermission('HRMS', 'VIEW') },
    { label: 'Studio Notice Board', path: '/hrms/announcements', icon: Megaphone, show: true },
  ];

  const financeItems = [
    { label: 'Finance Analytics', path: '/finance/dashboard', icon: Wallet, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Chart of Accounts', path: '/finance/coa', icon: BookOpen, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Transaction Ledger', path: '/finance/transactions', icon: DollarSign, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Invoices & Billing', path: '/finance/invoices', icon: Receipt, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Expense Claims', path: '/finance/expenses', icon: FileSpreadsheet, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Budgets & Variance', path: '/finance/budgets', icon: PieChart, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Owner Capital', path: '/finance/investments', icon: PiggyBank, show: hasPermission('FINANCE', 'VIEW') },
    { label: 'Financial Reports', path: '/finance/reports', icon: FileText, show: hasPermission('FINANCE', 'VIEW') },
  ];

  const platformItems = [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, show: true },
    { label: 'User Directory', path: '/users', icon: Users, show: hasPermission('USERS', 'VIEW') },
    { label: 'Roles & Access Matrix', path: '/roles', icon: ShieldCheck, show: hasPermission('ROLES', 'VIEW') },
    { label: 'Departments', path: '/departments', icon: Building2, show: hasPermission('DEPARTMENTS', 'VIEW') },
    { label: 'Audit Security Logs', path: '/audit', icon: FileText, show: hasPermission('AUDIT', 'VIEW') },
    { label: 'Company Settings', path: '/settings', icon: Settings, show: hasPermission('SETTINGS', 'VIEW') },
  ];

  return (
    <aside
      className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''} ${isHovered && collapsed ? 'sidebar-hover-expanded' : ''}`}
      onMouseEnter={() => collapsed && setIsHovered(true)}
      onMouseLeave={() => collapsed && setIsHovered(false)}
    >
      {/* Brand Header */}
      <div className="sidebar-brand">
        {isExpanded ? (
          <div className="brand-logo-expanded">
            <img
              src="/drishti-logo-icon.png"
              alt="Drishti Studios Mark"
              className="brand-logo-img"
            />
            <div className="brand-text">
              <span className="brand-title">Drishti Business OS</span>
              <span className="brand-sub">Enterprise Platform</span>
            </div>
          </div>
        ) : (
          <div className="brand-logo-collapsed" title="Drishti Business OS — Drishti Studios">
            <img
              src="/drishti-logo-icon.png"
              alt="Drishti Mark"
              className="brand-logo-icon-only"
            />
          </div>
        )}
      </div>

      {/* Navigation Menu */}
      <nav className="sidebar-nav">
        {isExpanded && <div className="nav-section-title">PROJECTS & OPERATIONS</div>}
        {projectItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} className="nav-icon" />
                {isExpanded && <span className="nav-label">{item.label}</span>}
              </NavLink>
            );
          })}

        <div className="nav-divider" />

        {isExpanded && <div className="nav-section-title">CRM & SALES PIPELINE</div>}
        {crmItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} className="nav-icon" />
                {isExpanded && <span className="nav-label">{item.label}</span>}
              </NavLink>
            );
          })}

        <div className="nav-divider" />

        {isExpanded && <div className="nav-section-title">TEAM MANAGEMENT</div>}
        {hrmsItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} className="nav-icon" />
                {isExpanded && <span className="nav-label">{item.label}</span>}
              </NavLink>
            );
          })}

        <div className="nav-divider" />

        {isExpanded && <div className="nav-section-title">FINANCE MODULE</div>}
        {financeItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} className="nav-icon" />
                {isExpanded && <span className="nav-label">{item.label}</span>}
              </NavLink>
            );
          })}

        <div className="nav-divider" />

        {isExpanded && <div className="nav-section-title">PLATFORM CORE</div>}
        {platformItems
          .filter((item) => item.show)
          .map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                title={!isExpanded ? item.label : undefined}
              >
                <Icon size={18} className="nav-icon" />
                {isExpanded && <span className="nav-label">{item.label}</span>}
              </NavLink>
            );
          })}
      </nav>

      {/* Footer / User Profile summary */}
      <div className="sidebar-footer">
        {isExpanded && user && (
          <div className="user-summary">
            <div className="user-avatar-mini">{user.name.charAt(0)}</div>
            <div className="user-meta">
              <span className="user-name">{user.name}</span>
              <span className="user-role-badge">{user.role?.name || user.role?.code}</span>
            </div>
          </div>
        )}
        <button className="collapse-toggle-btn" onClick={onToggleCollapse} title="Toggle Sidebar">
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>
    </aside>
  );
};
